import * as tslib_1 from "tslib";
// Core packages
import { OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
// Third party packages
import { MatDialog } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { finalize, take } from "rxjs/operators";
// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PermissionsService } from "src/app/modules/permissions/permissions.service";
import { RolesService } from "src/app/modules/roles/roles.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";
var RoleDetailComponent = /** @class */ (function () {
    /**
     * Class constructor
     */
    function RoleDetailComponent(configService, activatedRoute, router, toastrService, helperService, translateService, themeService, authService, dialog, cookieService, rolesService, permissionsService, apiService // DO NOT MOVE since it's used for 'autocomplete' field type
    ) {
        this.configService = configService;
        this.activatedRoute = activatedRoute;
        this.router = router;
        this.toastrService = toastrService;
        this.helperService = helperService;
        this.translateService = translateService;
        this.themeService = themeService;
        this.authService = authService;
        this.dialog = dialog;
        this.cookieService = cookieService;
        this.rolesService = rolesService;
        this.permissionsService = permissionsService;
        this.apiService = apiService;
        this.subscriptions = [];
        this.lang = this.translateService.currentLang || this.translateService.defaultLang;
        // BEGIN - Edit role form
        // BEGIN - ngx-formly
        this.form = new FormGroup({});
        this.model = {};
        this.fields = [];
        this.options = {};
        // END - ngx-formly
        this.formBtnOptions = {
            active: false,
            text: this.translateService.instant("common.cta.save"),
            spinnerSize: 19,
            raised: true,
            stroked: true,
            flat: false,
            fab: false,
            buttonColor: "primary",
            spinnerColor: "primary",
            fullWidth: false,
            disabled: false,
            mode: "indeterminate",
            buttonIcon: {
                fontSet: "fal",
                fontIcon: "fa-save",
                inline: true,
            },
        };
        this.deleteBtnOptions = {
            active: false,
            text: this.translateService.instant("common.cta.delete"),
            spinnerSize: 19,
            raised: true,
            stroked: true,
            flat: false,
            fab: false,
            buttonColor: "warn",
            spinnerColor: "warn",
            fullWidth: false,
            disabled: false,
            mode: "indeterminate",
            customClass: "",
            buttonIcon: {
                fontSet: "fal",
                fontIcon: "fa-trash",
                inline: true,
            },
        };
        this.detailCardIsLoading = true;
        this.deleteCardIsLoading = true;
    }
    /**
     * Init components
     *
     * @since 1.0.0
     */
    RoleDetailComponent.prototype.ngOnInit = function () {
        var _this = this;
        // Set page title
        this.themeService.setPageTitle(this.translateService.instant("roles.detail.pageTitle"));
        // Get logged user data
        this.subscriptions.push(this.authService.loggedUser$.subscribe(function (res) { return (_this.loggedUser = res); }));
        this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(function (res) { return (_this.loggedUserPermissions = res); }));
        // Get current lang
        this.lang =
            this.cookieService.get("lang") || this.configService.defaultLang;
        // Get requested role data
        var roleId = this.activatedRoute.snapshot.params.roleId;
        this.subscriptions.push(this.rolesService.get(roleId).subscribe(function (res) {
            if (res.status) {
                _this.role = res.data;
                // Stop loading in delete form
                _this.deleteCardIsLoading = false;
                _this.getPermissionsAndInitForm();
                // Make detail form card visible by disabling his card-spinner
                _this.detailCardIsLoading = false;
                return;
            }
            // Something bad happened
            // redirect user to home
            _this.toastrService.error(_this.translateService.instant("common.errors.unknown"));
            _this.router.navigate([_this.lang, "app", "roles", "list"]);
        }, function (err) {
            // if we came here users with given userId do not exist
            _this.helperService.handleError(err);
            _this.router.navigate([_this.lang, "errors", "404"]);
        }));
    };
    /**
     * Handle component destroy
     *
     * @since 1.0.0
     */
    RoleDetailComponent.prototype.ngOnDestroy = function () {
        // Unsubscribe from all subscriptions
        this.subscriptions.forEach(function (sub) { return sub.unsubscribe(); });
    };
    /**
     * Preload role's permissions data and then init form. If something
     * goes wrong just init the form
     *
     * @since 1.0.0
     */
    RoleDetailComponent.prototype.getPermissionsAndInitForm = function () {
        var _this = this;
        this.subscriptions.push(this.permissionsService
            .getList(0, 10, null, null, {
            ids: this.role.permissions.map(function (perm) { return perm.permissionId; }),
        })
            .subscribe(function (res) {
            if (res.status) {
                _this.initForm(res.data.items);
            }
            else {
                _this.initForm();
            }
        }, function (err) {
            _this.helperService.handleError(err);
            _this.initForm();
        }));
    };
    /**
     * Init form
     *
     * @since 1.0.0
     */
    RoleDetailComponent.prototype.initForm = function (availablePermissions) {
        var _this = this;
        // Set model
        this.model = this.role;
        // Get only permission ids
        this.role.permissions.forEach(function (perm, index) {
            _this.model.permissions[index] = perm.permissionId;
        });
        // Set fields
        this.fields = tslib_1.__spread(this.fields, [
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12 col-md-6",
                        type: "input",
                        key: "name",
                        templateOptions: {
                            label: this.translateService.instant("roles.fields.name"),
                            placeholder: this.translateService.instant("roles.fields.name"),
                            required: true,
                            minLength: 3,
                            maxLength: 50,
                        },
                    },
                    {
                        className: "col-12 col-md-6",
                        type: "select",
                        key: "type",
                        templateOptions: {
                            label: this.translateService.instant("roles.fields.type"),
                            placeholder: this.translateService.instant("roles.fields.type"),
                            required: true,
                            options: this.configService.availableRoleTypes,
                        },
                    },
                    {
                        className: "col-12",
                        type: "autocomplete",
                        key: "permissions",
                        templateOptions: {
                            label: this.translateService.instant("roles.fields.permissions"),
                            placeholder: this.translateService.instant("roles.fields.permissions"),
                            required: true,
                            bindLabel: "key",
                            bindValue: "_id",
                            multiple: true,
                            search$: this.permissionsService.getList.bind(this),
                            initialValues: availablePermissions,
                            hideSelected: true,
                            typeToSearchText: this.translateService.instant("common.words.typeToSearch"),
                            clearAllText: this.translateService.instant("common.words.clearAll"),
                            notFoundText: this.translateService.instant("common.words.notFound"),
                            loadingText: this.translateService.instant("common.words.loading"),
                        },
                    },
                    {
                        className: "col-12",
                        type: "textarea",
                        key: "description",
                        templateOptions: {
                            label: this.translateService.instant("roles.fields.description"),
                            placeholder: this.translateService.instant("roles.fields.description"),
                            maxLength: 500,
                            required: true,
                            rows: 2,
                        },
                        expressionProperties: {
                            "templateOptions.description": '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
                        },
                    },
                ],
            },
        ]);
    };
    /**
     * Handle form submit
     *
     * @since 1.0.0
     */
    RoleDetailComponent.prototype.onFormSubmit = function (model) {
        var _this = this;
        // Prevent invalid form submit
        if (this.form.invalid) {
            Object.keys(this.form.controls).forEach(function (key) {
                _this.form.controls[key].markAsTouched();
            });
            console.warn("form", this.form);
            return;
        }
        // Activate submit button loading status
        this.formBtnOptions.active = true;
        this.subscriptions.push(this.rolesService
            .update(this.role._id, model)
            .pipe(finalize(function () { return (_this.formBtnOptions.active = false); }))
            .subscribe(function (res) {
            if (res.status) {
                _this.toastrService.success(res.message);
            }
        }, function (err) {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            _this.helperService.handleFormlyError(_this.form, err);
        }));
    };
    /**
     * Remove current permission from database
     *
     * @since 1.0.0
     */
    RoleDetailComponent.prototype.onDelete = function () {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("dialogs.confirm.title"),
                message: this.translateService.instant("dialogs.confirm.message"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        // Subscribe to dialog result (only for 1 emit thanks to "take()")
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            if (!res) {
                return;
            }
            _this.subscriptions.push(_this.rolesService
                .delete(_this.role._id)
                .pipe(finalize(function () { return (_this.deleteBtnOptions.active = false); }))
                .subscribe(function (res2) {
                _this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                _this.router.navigate([_this.lang, "app", "roles", "list"]);
                return;
            }, function (err) {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                _this.helperService.handleError(err);
            }));
        });
    };
    return RoleDetailComponent;
}());
export { RoleDetailComponent };
